import { CreatePieceDto, PieceSummaryDto } from "@growth/dto";
import { CONTENT_FORMATS, ContentFormat, PIECE_STATUS_LABELS, PieceStatus } from "@growth/schema";
import { Badge, Button, Field, Input, Textarea } from "@growth/ui";
import { LoaderCircle, Sparkles } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useContentSettings, useCreatePiece, usePieces } from "../lib/content";
import { formatRelative } from "../lib/format";

const TONES: Partial<Record<PieceStatus, "success" | "accent">> = {
  approved: "accent",
  posted: "success",
  awaiting_approval: "accent",
};

export const StatusBadge = ({ status }: { status: PieceStatus }) => (
  <Badge
    tone={TONES[status] ?? "neutral"}
    className={
      status === "blocked" || status === "failed" ? "text-red-700 ring-red-200" : undefined
    }
  >
    {status === "drafting" && <LoaderCircle className="size-3 animate-spin" />}
    {PIECE_STATUS_LABELS[status]}
  </Badge>
);

/** What can be made: a channel and a format, on all eight platforms. */
const KINDS = (Object.keys(CONTENT_FORMATS) as ContentFormat[]).map((value) => ({
  value,
  label: CONTENT_FORMATS[value],
}));

export const kindLabel = (piece: { channel: string; format: string }) =>
  CONTENT_FORMATS[`${piece.channel}:${piece.format}` as ContentFormat] ?? piece.channel;

/** 9:16 for full-screen apps, 4:5 for feeds. */
export const isTall = (channel: string) => channel === "tiktok" || channel === "snapchat";

/** Ask the agents for a piece, and see everything they've made for this brand. */
export const ContentPage = () => {
  const { brand, atLeast } = useActive();
  const pieces = usePieces();

  return (
    <section className="max-w-5xl space-y-8">
      <PageHeader eyebrow={brand?.name ?? ""} title="Content">
        Give an idea; the writer (or, for carousels, the designer) drafts it, the claims check and
        the critic or art director review it, and it comes to you for approval. Nothing is posted
        without a person.
      </PageHeader>
      {atLeast("EDITOR") && <NewPiece />}
      {pieces.isError && <ErrorText>{apiErrorMessage(pieces.error)}</ErrorText>}
      <PieceList pieces={pieces.data} loading={pieces.isPending} />
    </section>
  );
};

const PieceList = ({ pieces, loading }: { pieces?: PieceSummaryDto[]; loading: boolean }) => (
  <div className="overflow-x-auto rounded-xl border border-line bg-white">
    <table className="w-full text-sm">
      <tbody className="divide-y divide-line">
        {loading && (
          <tr>
            <td className="px-4 py-6">
              <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
            </td>
          </tr>
        )}
        {pieces?.length === 0 && (
          <tr>
            <td className="px-4 py-8 text-center text-muted">
              Nothing yet. Start with an idea above.
            </td>
          </tr>
        )}
        {pieces?.map((piece) => (
          <tr key={piece.id} className="hover:bg-paper/50">
            <td className="w-28 px-4 py-3 align-top font-mono text-xs">
              <Link to={`/content/${piece.id}`} className="text-accent hover:underline">
                {piece.code}
              </Link>
            </td>
            <td className="px-4 py-3 align-top">
              <Link to={`/content/${piece.id}`} className="flex items-start gap-3">
                {piece.coverUrl && (
                  <img
                    src={piece.coverUrl}
                    alt=""
                    loading="lazy"
                    className={`${isTall(piece.channel) ? "aspect-[9/16]" : "aspect-[4/5]"} w-10 shrink-0 rounded border border-line object-cover`}
                  />
                )}
                <span className="min-w-0">
                  <p className="text-ink">{piece.hook || piece.idea}</p>
                  <p className="text-xs text-muted">
                    {kindLabel(piece)} · {piece.pillar} · {formatRelative(piece.updatedAt)}
                  </p>
                </span>
              </Link>
            </td>
            <td className="w-48 px-4 py-3 text-right align-top">
              <StatusBadge status={piece.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NewPiece = () => {
  const navigate = useNavigate();
  const settings = useContentSettings();
  const create = useCreatePiece();
  const pillars = settings.data?.pillars ?? [];
  const [form, setForm] = useState({
    kind: "linkedin:text",
    pillar: "",
    persona: "",
    idea: "",
  });
  const pillar = form.pillar || pillars[0]?.id || "general";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const [channel, format] = form.kind.split(":") as [
      CreatePieceDto["channel"],
      CreatePieceDto["format"],
    ];
    create.mutate(
      {
        channel,
        format,
        pillar,
        persona: form.persona,
        idea: form.idea,
      },
      { onSuccess: (piece) => navigate(`/content/${piece.id}`) },
    );
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      <h2 className="font-serif text-lg text-ink">New piece</h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="What to make">
          {(props) => (
            <select
              {...props}
              className={selectClass}
              value={form.kind}
              onChange={(event) => setForm({ ...form, kind: event.target.value })}
            >
              {KINDS.map((kind) => (
                <option key={kind.value} value={kind.value}>
                  {kind.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Pillar" hint={pillars.length ? undefined : "Add pillars in Content setup"}>
          {(props) => (
            <select
              {...props}
              className={selectClass}
              value={pillar}
              onChange={(event) => setForm({ ...form, pillar: event.target.value })}
            >
              {pillars.length === 0 && <option value="general">General</option>}
              {pillars.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="For whom (optional)" hint="A persona from Knowledge → Audience">
          {(props) => (
            <Input
              {...props}
              maxLength={200}
              value={form.persona}
              onChange={(event) => setForm({ ...form, persona: event.target.value })}
            />
          )}
        </Field>
      </div>
      <Field label="Idea" hint="One sentence is enough: the angle, the question, the story">
        {(props) => (
          <Textarea
            {...props}
            required
            minLength={5}
            maxLength={1000}
            rows={2}
            value={form.idea}
            onChange={(event) => setForm({ ...form, idea: event.target.value })}
          />
        )}
      </Field>
      {create.isError && <ErrorText>{apiErrorMessage(create.error)}</ErrorText>}
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? (
          <LoaderCircle className="size-4 animate-spin" />
        ) : (
          <Sparkles className="size-4" />
        )}
        {form.kind.endsWith(":carousel") ? "Design it" : "Draft it"}
      </Button>
    </form>
  );
};
